import React from "react";
import { Stack, Paper, Typography, Box, alpha, Skeleton } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

/**
 * Tarjeta de resumen por location mostrando assigned, unassigned, cross-location.
 */
function LocationCard({ location, selected, onClick }) {
  const total = location.assignedPatients + location.unassignedPatients;
  const assignedPct = total > 0 ? Math.round((location.assignedPatients / total) * 100) : 0;

  return (
    <Paper
      elevation={0}
      onClick={() => onClick(location)}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        cursor: "pointer",
        transition: "all 0.2s",
        bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.04) : "background.paper",
        "&:hover": {
          borderColor: "primary.main",
          transform: "translateY(-2px)",
          boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
        },
        minWidth: 200,
        flex: 1,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} noWrap>
        {location.centerName}
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <PeopleIcon sx={{ fontSize: 16, color: "#10B981" }} />
          <Typography variant="body2" fontWeight={600}>
            {location.assignedPatients}
          </Typography>
          <Typography variant="caption" color="text.secondary">assigned</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <PersonOffIcon sx={{ fontSize: 16, color: "#EF4444" }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: location.unassignedPatients > 0 ? "#EF4444" : "text.primary" }}>
            {location.unassignedPatients}
          </Typography>
          <Typography variant="caption" color="text.secondary">unassigned</Typography>
        </Stack>
        {location.crossLocationPatients > 0 && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <SwapHorizIcon sx={{ fontSize: 16, color: "#F59E0B" }} />
            <Typography variant="body2" fontWeight={600} color="#F59E0B">
              {location.crossLocationPatients}
            </Typography>
            <Typography variant="caption" color="text.secondary">cross-loc</Typography>
          </Stack>
        )}
      </Stack>
      {/* Progress bar */}
      <Box sx={{ mt: 1.5, height: 4, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.divider, 0.3), overflow: "hidden" }}>
        <Box sx={{ width: `${assignedPct}%`, height: "100%", borderRadius: 2, bgcolor: assignedPct > 90 ? "#10B981" : assignedPct > 50 ? "#F59E0B" : "#EF4444", transition: "width 0.3s" }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
        {assignedPct}% assigned
      </Typography>
    </Paper>
  );
}

export default function AssignmentSummary({ summary, loading, selectedLocationId, onSelectLocation }) {
  if (loading) {
    return (
      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 2 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={130} sx={{ flex: 1, minWidth: 200, borderRadius: 3 }} />
        ))}
      </Stack>
    );
  }

  if (!summary || summary.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">No location data available.</Typography>
    );
  }

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ rowGap: 2 }}>
      {summary.map((loc) => (
        <LocationCard
          key={loc.locationId}
          location={loc}
          selected={loc.locationId === selectedLocationId}
          onClick={onSelectLocation}
        />
      ))}
    </Stack>
  );
}
